"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { BuildAgentForFreeButton } from "~/components/marketplace/build-agent-for-free-button";
import { ContactSalesModal } from "~/components/marketing/contact-sales-modal";
import {
  MARKETING_BECOME_PARTNER,
  MARKETING_HIRE_ONBOARDING,
} from "~/lib/marketplace/marketing-nav-config";
import { PublicAuthFooterLink } from "~/components/marketing/public-auth-nav-link";
import {
  MARKETING_HOME,
  MARKETING_PRODUCT_NAV,
  SITEGLUE_PRODUCT_NAV,
  MARKETING_RESOURCES_NAV,
  MARKETING_SOLUTIONS_NAV,
  inboundGptHref,
} from "~/lib/inbound-gpt/paths";
import { marketingSiteUrl } from "~/lib/auth-paths";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import {
  SITEGLUE_CTA_CONFIG,
  trackSiteGlueCta,
} from "~/lib/siteglue/cta-config";
import { SITEGLUE_AI_TEAM } from "~/lib/siteglue/siteglue-ai-team";
import { SITEGLUE_INDUSTRIES_NAV } from "~/lib/siteglue/siteglue-industries-nav";
import {
  SITEGLUE_SOLUTION_CATEGORIES,
  SITEGLUE_SOLUTIONS_BASE_PATH,
} from "~/lib/siteglue/siteglue-solutions-nav";
import { cn } from "~/lib/utils";

type MarketingFooterProps = {
  /** If true, use in-page anchors; otherwise link back to landing sections. */
  isLanding?: boolean;
  /** Force brand during SSR (client getMarketingChrome defaults to EasyGPT without window). */
  brandId?: "siteglue" | "inbound_gpt";
};

const SITEGLUE_PLATFORM_EXTRA = [
  { label: "Human-in-the-Loop™", href: "/human-in-the-loop" },
  { label: "Team Handoff™", href: "/team-handoff" },
  { label: "Team Takeover™", href: "/team-takeover" },
  { label: "Phone Handling Rules", href: "/phone-handling-rules" },
] as const;

function scrollTop() {
  if (typeof window !== "undefined") window.scrollTo(0, 0);
}

/**
 * Canonical marketing footer for SiteGlue and Inbound GPT.
 * Used by homepage/about (standalone) and marketing-public shell (blog, platform, etc.).
 */
export default function MarketingFooter({
  isLanding = false,
  brandId,
}: MarketingFooterProps) {
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const chrome = getMarketingChrome(
    brandId === "siteglue"
      ? "www.siteglue.ai"
      : brandId === "inbound_gpt"
        ? "www.easygpt.builders"
        : undefined,
  );
  const isSiteGlue = chrome.brandId === "siteglue";

  const productLinks = isSiteGlue
    ? [...SITEGLUE_PRODUCT_NAV, ...SITEGLUE_PLATFORM_EXTRA]
    : [
        { label: "Browse Agents", href: "/agents" },
        ...MARKETING_PRODUCT_NAV.map((item) =>
          item.label === "How It Works" && isLanding
            ? { ...item, href: "#how-it-works" }
            : item,
        ),
      ];

  const resourceLinks = MARKETING_RESOURCES_NAV;

  const openContactSales = () => {
    if (isSiteGlue) {
      trackSiteGlueCta({
        action: "contactSales",
        label: SITEGLUE_CTA_CONFIG.contactSales.label,
        location: "footer",
        destination: "contact-sales-modal",
      });
    }
    setContactSalesOpen(true);
  };

  return (
    <>
      <footer
        data-testid="footer"
        className="w-full border-t border-border bg-muted/30 py-12 sm:py-16"
      >
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
          <div
            className={cn(
              "grid grid-cols-1 gap-10 sm:grid-cols-2 lg:gap-8",
              isSiteGlue ? "lg:grid-cols-3 xl:grid-cols-6" : "lg:grid-cols-5",
            )}
          >
            <div className="text-left">
              <Link
                href={marketingSiteUrl(MARKETING_HOME)}
                className="inline-flex items-center gap-2"
                onClick={scrollTop}
              >
                <Image
                  src={chrome.logoSrc}
                  alt={chrome.logoAlt}
                  width={28}
                  height={28}
                  className="h-7 w-7 rounded-md"
                />
                <div className="flex flex-col items-start text-left">
                  {isSiteGlue ? (
                    <span className="text-lg font-bold leading-none tracking-tight">
                      <span style={{ color: chrome.primaryColor }}>Site</span>
                      <span style={{ color: chrome.accentColor }}>Glue</span>
                    </span>
                  ) : (
                    <span className="text-lg font-bold leading-none tracking-tight">
                      {chrome.productName}
                    </span>
                  )}
                  <span className="mt-0.5 text-[9px] leading-none text-muted-foreground">
                    {chrome.tagline}
                  </span>
                </div>
              </Link>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
                {isSiteGlue
                  ? "Your agent answers calls and website visitors, captures or schedules appointments when a calendar is connected, and routes people to your team."
                  : "AI-powered patient communication for healthcare practices. Never miss a call, book appointments 24/7."}
              </p>
            </div>

            <div className="text-left">
              <h3 className="mb-3 text-sm font-semibold">
                {isSiteGlue ? "Platform" : "Product"}
              </h3>
              <ul className="space-y-2">
                {productLinks.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={inboundGptHref(item.href)}
                      onClick={scrollTop}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-left">
              <h3 className="mb-3 text-sm font-semibold">
                {isSiteGlue ? "Industries" : "Agent Roles"}
              </h3>
              <ul className="space-y-2">
                {(isSiteGlue ? SITEGLUE_INDUSTRIES_NAV : MARKETING_SOLUTIONS_NAV).map((item) => (
                  <li key={item.href}>
                    <Link
                      href={inboundGptHref(item.href)}
                      onClick={scrollTop}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {isSiteGlue ? item.navLabel : item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {isSiteGlue ? (
              <div className="text-left">
                <h3 className="mb-3 text-sm font-semibold">Solutions</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      href={inboundGptHref(SITEGLUE_SOLUTIONS_BASE_PATH)}
                      onClick={scrollTop}
                      className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      All Business Outcomes
                    </Link>
                  </li>
                  {SITEGLUE_SOLUTION_CATEGORIES.map((category) => (
                    <li key={category.id}>
                      <Link
                        href={inboundGptHref(`${SITEGLUE_SOLUTIONS_BASE_PATH}#${category.id}`)}
                        onClick={scrollTop}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {category.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="text-left">
              <h3 className="mb-3 text-sm font-semibold">Resources</h3>
              <ul className="space-y-2">
                {resourceLinks.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={inboundGptHref(item.href)}
                      onClick={scrollTop}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-left">
              <h3 className="mb-3 text-sm font-semibold">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    href={inboundGptHref("/about")}
                    onClick={scrollTop}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    About
                  </Link>
                </li>
                {isSiteGlue ? (
                  <li>
                    <Link
                      href={inboundGptHref("/about/my-team")}
                      onClick={scrollTop}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      My Team
                    </Link>
                  </li>
                ) : null}
                <li>
                  <Link
                    href={inboundGptHref("/contact")}
                    onClick={scrollTop}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={openContactSales}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {isSiteGlue ? SITEGLUE_CTA_CONFIG.contactSales.label : "Contact Sales"}
                  </button>
                </li>
                <li>
                  <Link
                    href={MARKETING_BECOME_PARTNER}
                    onClick={scrollTop}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Partners
                  </Link>
                </li>
                {!isSiteGlue ? (
                  <>
                    <li>
                      <Link
                        href={MARKETING_HIRE_ONBOARDING}
                        onClick={scrollTop}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Post an AI Job
                      </Link>
                    </li>
                    <li>
                      <BuildAgentForFreeButton
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-sm font-normal text-muted-foreground hover:text-foreground"
                        testId="link-footer-build-agent-free"
                      />
                    </li>
                  </>
                ) : null}
                <li>
                  <PublicAuthFooterLink className="text-sm text-muted-foreground hover:text-foreground" />
                </li>
                <li>
                  <Link
                    href={inboundGptHref("/privacy-policy")}
                    onClick={scrollTop}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    href={inboundGptHref("/terms-of-service")}
                    onClick={scrollTop}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {isSiteGlue ? (
            <div className="mt-10 rounded-2xl border border-border bg-background p-5 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold">Trish · SiteGlue onboarding</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Direct line{" "}
                  <a className="font-medium text-foreground" href={`tel:${SITEGLUE_AI_TEAM[0]?.phoneDisplay?.replace(/\D/g, "")}`}>
                    {SITEGLUE_AI_TEAM[0]?.phoneDisplay}
                  </a>
                  {" · "}
                  <a className="font-medium text-foreground" href={`mailto:${SITEGLUE_AI_TEAM[0]?.email}`}>
                    {SITEGLUE_AI_TEAM[0]?.email}
                  </a>
                </p>
              </div>
            </div>
          ) : null}

          <div className="mt-12 flex w-full flex-col items-center justify-center gap-4 border-t border-border pt-8 text-center sm:flex-row sm:justify-between sm:text-left">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {chrome.companyLegalName}. All rights
              reserved.
              {!isSiteGlue ? " HIPAA Compliant." : null}
            </p>
            <div className="flex items-center gap-4">
              <Link
                href="/.well-known/mcp.json"
                className="text-xs font-medium text-foreground transition-colors hover:text-foreground"
              >
                For AI agents
              </Link>
              <Link
                href={inboundGptHref("/privacy-policy")}
                onClick={scrollTop}
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Privacy Policy
              </Link>
              <Link
                href={inboundGptHref("/terms-of-service")}
                onClick={scrollTop}
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Terms of Service
              </Link>
              {!isSiteGlue ? (
                <Link
                  href={inboundGptHref("/baa")}
                  onClick={scrollTop}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  BAA
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </footer>

      <ContactSalesModal open={contactSalesOpen} onOpenChange={setContactSalesOpen} />
    </>
  );
}
